/**
 * Reset Password Page - Reconflux
 *
 * Set a new password after clicking the reset link.
 * "Create a new key to your sanctuary."
 */

import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Logo } from '../../components/ui/Logo'
import { useAuth } from '../../providers/AuthProvider'
import { marketingHomeUrl } from '../../lib/marketingSite'

export function ResetPasswordPage() {
  const { t, i18n } = useTranslation('auth')
  const navigate = useNavigate()
  const { updatePassword } = useAuth()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('resetPassword.errorPasswordsMismatch'))
      return
    }

    // Validate password strength
    if (password.length < 8) {
      setError(t('resetPassword.errorPasswordLength'))
      return
    }

    setIsLoading(true)

    try {
      const { error } = await updatePassword(password)

      if (error) {
        setError(error.message)
        return
      }

      setSuccess(true)

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 2000)
    } catch {
      setError(t('errors.unexpected'))
    } finally {
      setIsLoading(false)
    }
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-dvh bg-surface-page flex flex-col">
        <header className="p-6">
          <Logo href={marketingHomeUrl(i18n.resolvedLanguage)} />
        </header>

        <main className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            {/* Success icon */}
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-semibold text-ink mb-2">
              {t('resetPassword.success.title')}
            </h1>
            <p className="text-ink-subtle mb-6">
              {t('resetPassword.success.body')}
            </p>

            {/* Loading indicator */}
            <div className="flex items-center justify-center gap-2 text-accent">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-sm">{t('resetPassword.success.redirecting')}</span>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-surface-page flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Logo href={marketingHomeUrl(i18n.resolvedLanguage)} />
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Header text */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-ink mb-2">
              {t('resetPassword.title')}
            </h1>
            <p className="text-ink-subtle text-sm">
              {t('resetPassword.subtitle')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error message */}
            {error && (
              <div className="bg-danger/10 border border-danger/30 rounded-lg px-4 py-3">
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            {/* Password field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm text-ink-secondary mb-1.5"
              >
                {t('resetPassword.newPasswordLabel')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                autoFocus
                className="w-full px-4 py-3 bg-surface-raised border border-edge/50 rounded-lg
                  text-ink placeholder-ink-muted
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  transition-colors"
                placeholder={t('resetPassword.newPasswordPlaceholder')}
              />
            </div>

            {/* Confirm password field */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm text-ink-secondary mb-1.5"
              >
                {t('resetPassword.confirmPasswordLabel')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-surface-raised border border-edge/50 rounded-lg
                  text-ink placeholder-ink-muted
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  transition-colors"
                placeholder={t('resetPassword.confirmPasswordPlaceholder')}
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-accent text-accent-contrast font-medium rounded-lg
                hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-surface-page
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {t('resetPassword.submitting')}
                </span>
              ) : (
                t('resetPassword.submit')
              )}
            </button>
          </form>
        </div>
      </main>
    </div>
  )
}
